import styled, { css } from 'styled-components';

export type PillVariant = 'ink' | 'accent' | 'paper' | 'outline' | 'outlineOnDark';
export type PillSize = 'sm' | 'md' | 'lg';

const sizes: Record<PillSize, ReturnType<typeof css>> = {
  sm: css`
    padding: 11px 20px;
    font-size: 0.94rem;
    gap: 8px;
  `,
  md: css`
    padding: 16px 30px;
    font-size: 1.02rem;
    gap: 10px;
  `,
  lg: css`
    padding: 18px 36px;
    font-size: 1.05rem;
    gap: 10px;
  `
};

const variants: Record<PillVariant, ReturnType<typeof css>> = {
  ink: css`
    background: ${({ theme }) => theme.colors.ink};
    color: ${({ theme }) => theme.colors.paper};

    &:hover {
      background: ${({ theme }) => theme.colors.accent};
      color: ${({ theme }) => theme.colors.white};
    }
  `,
  accent: css`
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.white};

    &:hover {
      color: ${({ theme }) => theme.colors.white};
    }
  `,
  paper: css`
    background: ${({ theme }) => theme.colors.paper};
    color: ${({ theme }) => theme.colors.ink};

    &:hover {
      background: ${({ theme }) => theme.colors.accent};
      color: ${({ theme }) => theme.colors.white};
    }
  `,
  outline: css`
    border: 1px solid ${({ theme }) => theme.colors.line};
    color: ${({ theme }) => theme.colors.ink};

    &:hover {
      border-color: ${({ theme }) => theme.colors.ink};
      color: ${({ theme }) => theme.colors.ink};
    }
  `,
  outlineOnDark: css`
    border: 1px solid ${({ theme }) => theme.colors.onInk.border};
    color: ${({ theme }) => theme.colors.paper};

    &:hover {
      border-color: ${({ theme }) => theme.colors.paper};
      color: ${({ theme }) => theme.colors.paper};
    }
  `
};

const pillBase = css<{ $variant: PillVariant; $size?: PillSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 100px;
  font-family: inherit;
  font-weight: 600;
  white-space: nowrap;
  transition: transform 0.25s ease, background 0.25s ease, border-color 0.25s ease, color 0.25s ease;

  ${({ $size = 'md' }) => sizes[$size]}
  ${({ $variant }) => variants[$variant]}

  &:hover {
    transform: translateY(${({ $size = 'md' }) => ($size === 'sm' ? '-2px' : '-3px')});
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 3px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const Pill = styled.a<{ $variant: PillVariant; $size?: PillSize }>`
  ${pillBase}
`;

export const PillButton = styled.button<{ $variant: PillVariant; $size?: PillSize }>`
  ${pillBase}
`;

export const PillArrow = styled.span.attrs({ 'aria-hidden': true })`
  font-family: ${({ theme }) => theme.fonts.mono};
`;
